<template>
    <div class="webphone-call-history">
        <div class="history-header">
            <h4>Arama Geçmişi</h4>
            <button 
                v-if="callHistory.length > 0"
                @click="$emit('clear-history')"
                class="clear-btn"
            >
                <i class="material-icons">delete_sweep</i>
            </button>
        </div>

        <div v-if="callHistory.length === 0" class="empty-state">
            <div class="empty-icon">
                <i class="material-icons">history</i>
            </div>
            <div class="empty-text">Henüz arama geçmişi yok</div>
        </div>

        <div v-else class="history-list">
            <div 
                v-for="(call, index) in callHistory" 
                :key="index"
                class="history-item"
                @click="$emit('call', call)"
            >
                <div class="call-icon">
                    <i class="material-icons" :class="getCallIconClass(call.type)">
                        {{ getCallIcon(call.type) }}
                    </i>
                </div>
                
                <div class="call-info">
                    <div class="call-number">
                        {{ call.name || call.number }}
                    </div>
                    <div class="call-details">
                        <span class="call-date">{{ formatDate(call.date) }}</span>
                        <span v-if="call.duration > 0" class="call-duration">
                            • {{ formatDuration(call.duration) }}
                        </span>
                    </div>
                </div>
                
                <div class="call-actions">
                    <button 
                        class="action-btn info-btn"
                        @click.stop="showCallInfo(call)"
                    >
                        <i class="material-icons">info</i>
                    </button>
                    <button 
                        class="action-btn call-btn"
                        @click.stop="$emit('call', call)"
                    >
                        <i class="material-icons">call</i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Call Info Modal -->
        <div v-if="selectedCall" class="call-info-modal" @click.self="selectedCall = null">
            <div class="modal-content">
                <div class="modal-header">
                    <h4>Arama Detayları</h4>
                    <button @click="selectedCall = null" class="close-btn">
                        <i class="material-icons">close</i>
                    </button>
                </div>
                
                <div class="modal-body">
                    <div class="info-row">
                        <span class="label">Numara:</span>
                        <span class="value">{{ selectedCall.number }}</span>
                    </div>
                    
                    <div v-if="selectedCall.name" class="info-row">
                        <span class="label">İsim:</span>
                        <span class="value">{{ selectedCall.name }}</span>
                    </div>
                    
                    <div class="info-row">
                        <span class="label">Tür:</span>
                        <span class="value call-type" :class="selectedCall.type">
                            {{ getCallTypeText(selectedCall.type) }}
                        </span>
                    </div>
                    
                    <div class="info-row">
                        <span class="label">Tarih:</span>
                        <span class="value">{{ formatFullDate(selectedCall.date) }}</span>
                    </div>
                    
                    <div class="info-row">
                        <span class="label">Süre:</span>
                        <span class="value">{{ formatDuration(selectedCall.duration) }}</span>
                    </div>
                </div>
                
                <div class="modal-actions">
                    <button @click="selectedCall = null" class="cancel-btn">
                        Kapat
                    </button>
                    <button @click="callFromModal" class="call-btn">
                        <i class="material-icons">call</i>
                        Ara
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";

export default Vue.extend({
    name: "WebPhoneCallHistory",
    props: {
        callHistory: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            selectedCall: null,
        };
    },
    methods: {
        getCallIcon(type) {
            switch (type) {
                case 'incoming':
                    return 'call_received';
                case 'outgoing':
                    return 'call_made';
                case 'missed':
                    return 'call_received';
                default:
                    return 'call';
            }
        },
        
        getCallIconClass(type) {
            return `call-${type}`;
        },
        
        getCallTypeText(type) {
            switch (type) {
                case 'incoming':
                    return 'Gelen Arama';
                case 'outgoing':
                    return 'Giden Arama';
                case 'missed':
                    return 'Cevapsız Arama';
                default:
                    return 'Arama';
            }
        },
        
        formatDate(date) {
            const now = moment();
            const callMoment = moment(date);
            
            if (now.isSame(callMoment, 'day')) {
                return callMoment.format('HH:mm');
            } else if (now.diff(callMoment, 'days') === 1) {
                return 'Dün ' + callMoment.format('HH:mm');
            } else if (now.diff(callMoment, 'days') < 7) {
                return callMoment.format('dddd HH:mm');
            } else {
                return callMoment.format('DD.MM.YYYY');
            }
        },
        
        formatFullDate(date) {
            return moment(date).format('DD.MM.YYYY HH:mm:ss');
        },
        
        formatDuration(seconds) {
            if (!seconds || seconds === 0) {
                return '00:00';
            }
            
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = seconds % 60;
            
            if (hours > 0) {
                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            } else {
                return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            }
        },
        
        showCallInfo(call) {
            this.selectedCall = call;
        },
        
        callFromModal() {
            if (this.selectedCall) {
                this.$emit('call', this.selectedCall);
                this.selectedCall = null;
            }
        },
    },
});
</script>

<style scoped lang="scss">
.webphone-call-history {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 20px;
}

.history-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    h4 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #1C1C1E;
    }
    
    .clear-btn {
        background: none;
        border: none;
        padding: 8px;
        border-radius: 8px;
        cursor: pointer;
        color: #FF3B30;
        
        &:hover {
            background: rgba(255, 59, 48, 0.1);
        }
        
        .material-icons {
            font-size: 20px;
        }
    }
}

.empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #8E8E93;
    
    .empty-icon {
        margin-bottom: 12px;
        
        .material-icons {
            font-size: 48px;
            color: #C7C7CC;
        }
    }
    
    .empty-text {
        font-size: 14px;
        text-align: center;
    }
}

.history-list {
    flex: 1;
    overflow-y: auto;
    
    .history-item {
        display: flex;
        align-items: center;
        padding: 12px 0;
        border-bottom: 1px solid #F2F2F7;
        cursor: pointer;
        transition: background-color 0.2s ease;
        
        &:hover {
            background: rgba(0, 0, 0, 0.03);
        }
        
        &:last-child {
            border-bottom: none;
        }
        
        .call-icon {
            flex: 0 0 auto;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 12px;
            
            .material-icons {
                font-size: 18px;
                
                &.call-incoming {
                    color: #34C759;
                }
                
                &.call-outgoing {
                    color: #007AFF;
                }
                
                &.call-missed {
                    color: #FF3B30;
                }
            }
        }
        
        .call-info {
            flex: 1;
            min-width: 0;
            
            .call-number {
                font-size: 15px;
                font-weight: 500;
                color: #1C1C1E;
                margin-bottom: 2px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            
            .call-details {
                font-size: 12px;
                color: #8E8E93;
                
                .call-date {
                    
                }
                
                .call-duration {
                    
                }
            }
        }
        
        .call-actions {
            flex: 0 0 auto;
            display: flex;
            gap: 8px;
            opacity: 0;
            transition: opacity 0.2s ease;
            
            .action-btn {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                border: none;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: all 0.2s ease;
                
                .material-icons {
                    font-size: 16px;
                    color: white;
                }
                
                &.info-btn {
                    background: #8E8E93;
                    
                    &:hover {
                        background: #6D6D70;
                    }
                }
                
                &.call-btn {
                    background: #34C759;
                    
                    &:hover {
                        background: #2FB653;
                    }
                }
            }
        }
        
        &:hover .call-actions {
            opacity: 1;
        }
    }
}

.call-info-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    
    .modal-content {
        background: white;
        border-radius: 12px;
        width: 90%;
        max-width: 400px;
        max-height: 80vh;
        overflow: hidden;
        
        .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px;
            border-bottom: 1px solid #F2F2F7;
            
            h4 {
                margin: 0;
                font-size: 16px;
                font-weight: 600;
                color: #1C1C1E;
            }
            
            .close-btn {
                background: none;
                border: none;
                padding: 4px;
                border-radius: 4px;
                cursor: pointer;
                color: #8E8E93;
                
                &:hover {
                    background: rgba(0, 0, 0, 0.05);
                }
                
                .material-icons {
                    font-size: 20px;
                }
            }
        }
        
        .modal-body {
            padding: 20px;
            
            .info-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 8px 0;
                border-bottom: 1px solid #F2F2F7;
                
                &:last-child {
                    border-bottom: none;
                }
                
                .label {
                    font-size: 14px;
                    color: #8E8E93;
                    font-weight: 500;
                }
                
                .value {
                    font-size: 14px;
                    color: #1C1C1E;
                    font-weight: 500;
                    text-align: right;
                    
                    &.call-type {
                        &.incoming {
                            color: #34C759;
                        }
                        
                        &.outgoing {
                            color: #007AFF;
                        }
                        
                        &.missed {
                            color: #FF3B30;
                        }
                    }
                }
            }
        }
        
        .modal-actions {
            display: flex;
            gap: 12px;
            padding: 20px;
            border-top: 1px solid #F2F2F7;
            
            button {
                flex: 1;
                padding: 12px;
                border: none;
                border-radius: 8px;
                font-weight: 600;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                
                &.cancel-btn {
                    background: #F2F2F7;
                    color: #1C1C1E;
                    
                    &:hover {
                        background: #E5E5EA;
                    }
                }
                
                &.call-btn {
                    background: #007AFF;
                    color: white;
                    
                    &:hover {
                        background: #0056CC;
                    }
                    
                    .material-icons {
                        font-size: 18px;
                    }
                }
            }
        }
    }
}

// Scrollbar styling
.history-list {
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #C7C7CC;
        border-radius: 3px;
        
        &:hover {
            background: #A1A1A6;
        }
    }
}

// Dark mode support
@media (prefers-color-scheme: dark) {
    .webphone-call-history {
        .history-header h4 {
            color: white;
        }
        
        .history-list .history-item {
            border-bottom-color: #2C2C2E;
            
            &:hover {
                background: rgba(255, 255, 255, 0.05);
            }
            
            .call-info .call-number {
                color: white;
            }
        }
        
        .call-info-modal .modal-content {
            background: #1C1C1E;
            
            .modal-header {
                border-bottom-color: #2C2C2E;
                
                h4 {
                    color: white;
                }
                
                .close-btn {
                    color: #8E8E93;
                    
                    &:hover {
                        background: rgba(255, 255, 255, 0.05);
                    }
                }
            }
            
            .modal-body .info-row {
                border-bottom-color: #2C2C2E;
                
                .value {
                    color: white;
                }
            }
            
            .modal-actions {
                border-top-color: #2C2C2E;
                
                .cancel-btn {
                    background: #2C2C2E;
                    color: white;
                    
                    &:hover {
                        background: #3A3A3C;
                    }
                }
            }
        }
    }
    
    .history-list {
        &::-webkit-scrollbar-thumb {
            background: #48484A;
            
            &:hover {
                background: #5E5E60;
            }
        }
    }
}
</style> 